import { Button } from "@izicvs/ui";
import { cn, templatesList } from "@izicvs/utils";
import { t } from "@lingui/macro";
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";

type TemplateCategory = "Minimaliste" | "Professionnel" | "Créatif";
type TemplateCategoriesMap = Record<TemplateCategory, string[]>;

// One mechanism only (native scrollLeft) drives everything: the continuous
// auto-scroll, hover-pause, prev/next and the pagination dots. The list is
// rendered twice so the scroll loops seamlessly (when we pass the halfway point
// we jump back by half onto an identical frame, invisibly).
const SCROLL_SPEED = 0.8; // px per frame (~48px/s at 60fps)
const PAGES = 6; // pagination dots

export const TemplatesSection = (): React.JSX.Element => {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const leftColumnRef = useRef<HTMLDivElement | null>(null);
  const resumeTimeoutRef = useRef<number | null>(null);

  const [hovered, setHovered] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(true);
  const [isHovering, setIsHovering] = useState<boolean>(false);
  const [interacting, setInteracting] = useState<boolean>(false);
  const [activeDot, setActiveDot] = useState<number>(0);

  useEffect(() => {
    const checkMobile = (): void => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();

    let resizeTimeout: number;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = window.setTimeout(checkMobile, 100);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeTimeout);
    };
  }, []);

  // Keep the slider exactly as tall as the left content column on desktop.
  useLayoutEffect(() => {
    const el = leftColumnRef.current;
    if (!el) return;

    const measure = () => {
      setContentHeight(el.offsetHeight);
    };
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, []);

  // Continuous auto-scroll. Paused while hovering, during manual nav, or when
  // the user paused playback.
  useEffect(() => {
    if (!isPlaying || isHovering || interacting) return;

    let raf = 0;
    const step = () => {
      const el = trackRef.current;
      if (el) {
        const half = el.scrollWidth / 2;
        if (half > 0) {
          el.scrollLeft += SCROLL_SPEED;
          if (el.scrollLeft >= half) el.scrollLeft -= half;
        }
      }
      raf = window.requestAnimationFrame(step);
    };

    raf = window.requestAnimationFrame(step);
    return () => {
      window.cancelAnimationFrame(raf);
    };
  }, [isPlaying, isHovering, interacting]);

  const onScroll = (): void => {
    const el = trackRef.current;
    if (!el) return;
    const half = el.scrollWidth / 2 || 1;
    const progress = (el.scrollLeft % half) / half; // 0..1 within one set
    setActiveDot(Math.round(progress * PAGES) % PAGES);
  };

  const pauseThenResume = (): void => {
    setInteracting(true);
    if (resumeTimeoutRef.current) window.clearTimeout(resumeTimeoutRef.current);
    resumeTimeoutRef.current = window.setTimeout(() => {
      setInteracting(false);
    }, 3000);
  };

  const nudge = (direction: number): void => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.85, behavior: "smooth" });
    pauseThenResume();
  };

  const goToDot = (index: number): void => {
    const el = trackRef.current;
    if (!el) return;
    const half = el.scrollWidth / 2;
    el.scrollTo({ left: (half / PAGES) * index, behavior: "smooth" });
    setActiveDot(index);
    pauseThenResume();
  };

  useEffect(() => {
    return () => {
      if (resumeTimeoutRef.current) window.clearTimeout(resumeTimeoutRef.current);
    };
  }, []);

  const templateCategories: TemplateCategoriesMap = useMemo(
    () => ({
      Minimaliste: templatesList.slice(0, Math.ceil(templatesList.length / 3)),
      Professionnel: templatesList.slice(
        Math.ceil(templatesList.length / 3),
        Math.ceil((templatesList.length * 2) / 3),
      ),
      Créatif: templatesList.slice(Math.ceil((templatesList.length * 2) / 3)),
    }),
    [],
  );

  const getCategoryDescription = (category: TemplateCategory): string => {
    switch (category) {
      case "Minimaliste": {
        return t`Clean and efficient, perfect for experienced profiles.`;
      }
      case "Professionnel": {
        return t`Classic and structured, ideal for traditional sectors.`;
      }
      case "Créatif": {
        return t`Original and distinctive, suitable for creative industries.`;
      }
      default: {
        return "";
      }
    }
  };

  const getCategoryLabel = (category: TemplateCategory): string => {
    switch (category) {
      case "Minimaliste": {
        return t`Minimalist`;
      }
      case "Professionnel": {
        return t`Professional`;
      }
      case "Créatif": {
        return t`Creative`;
      }
      default: {
        return category;
      }
    }
  };

  // Preload the first images to avoid flashes as they scroll in.
  useEffect(() => {
    for (const [index, template] of templatesList.entries()) {
      if (index < 8) {
        const img = new Image();
        img.src = `/templates/jpg/${template}.jpg`;
      }
    }
  }, []);

  // Duplicate the list so the scroll loops seamlessly.
  const marqueeItems = [...templatesList, ...templatesList];

  return (
    <section id="templates" className="relative overflow-hidden py-12">
      {/* Soft, on-brand decorative glow */}
      <div className="animate-pulse-slow absolute right-1/4 top-1/3 -z-10 size-96 rounded-full bg-primary/[0.06] opacity-60 blur-3xl" />
      <div className="animate-pulse-slow animation-delay-2000 absolute bottom-1/4 left-1/3 -z-10 size-80 rounded-full bg-primary/[0.04] opacity-50 blur-3xl" />

      <div className="container">
        <div className="flex flex-col gap-12 lg:flex-row lg:items-start">
          <div ref={leftColumnRef} className="z-10 space-y-4 lg:mt-12 lg:basis-96">
            <div className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary shadow-sm transition-colors duration-300 hover:bg-primary/15">
              <svg className="mr-2 size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
              {t`Premium Templates`}
            </div>

            <h2 className="bg-gradient-to-br from-foreground via-foreground to-primary bg-clip-text text-4xl font-bold leading-[1.1] tracking-tight text-transparent lg:text-5xl">
              {t`Templates that make you stand out`}
            </h2>

            <p className="text-sm leading-relaxed text-foreground/70">
              {t`Izicvs offers a library of professional templates optimized for ATS. Each template is designed to highlight your background and increase your chances of landing an interview.`}
            </p>

            <div className="hidden lg:block">
              <div className="grid grid-cols-1">
                {Object.entries(templateCategories).map(([category, templates], index) => (
                  <div key={category} className="flex flex-col gap-1.5 rounded-xl p-3">
                    <h3 className="flex items-center text-lg font-semibold">
                      <span className="mr-2 flex size-6 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                        {index + 1}
                      </span>
                      {getCategoryLabel(category as TemplateCategory)}
                      <span className="ml-2 text-sm font-normal text-foreground/40">
                        ({templates.length})
                      </span>
                    </h3>
                    <p className="ml-8 text-sm text-foreground/60">
                      {getCategoryDescription(category as TemplateCategory)}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-4 pt-4 sm:flex-row">
              <Link to={"/auth/register"}>
                <Button className="group px-6 py-2.5 shadow-lg shadow-primary/20 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-primary/25">
                  <span className="flex items-center">
                    {t`Discover more`}
                    <svg
                      className="ml-2 size-5 transition-transform duration-300 group-hover:translate-x-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                      />
                    </svg>
                  </span>
                </Button>
              </Link>
              <Link to={"/auth/register"}>
                <Button
                  variant="outline"
                  className="border-2 px-6 py-2.5 transition-colors duration-200 hover:bg-secondary"
                >
                  {t`Create my resume`}
                </Button>
              </Link>
            </div>
          </div>

          {/* Auto-scrolling previews: pause on hover, driven purely by scrollLeft. */}
          <div
            ref={trackRef}
            onScroll={onScroll}
            onMouseEnter={() => {
              setIsHovering(true);
            }}
            onMouseLeave={() => {
              setIsHovering(false);
            }}
            className="relative w-full overflow-hidden lg:absolute lg:right-0 lg:h-[520px] lg:max-w-[60%]"
            style={isMobile ? undefined : { height: contentHeight ?? undefined }}
          >
            <div className="flex w-max items-stretch gap-x-6 py-6 lg:h-full">
              {marqueeItems.map((template, index) => {
                const dimmed = hovered !== null && hovered !== template;

                return (
                  <a
                    key={`${template}-${index}`}
                    data-tpl-card
                    target="_blank"
                    rel="noreferrer"
                    href={`templates/pdf/${template}.pdf`}
                    onMouseEnter={() => {
                      setHovered(template);
                    }}
                    onMouseLeave={() => {
                      setHovered(null);
                    }}
                    className={cn(
                      "group/card relative block h-[360px] shrink-0 overflow-hidden ring-1 ring-slate-900/[0.07] transition-[transform,opacity,box-shadow,filter] duration-300 dark:ring-white/10 lg:h-full",
                      dimmed
                        ? "scale-[0.96] opacity-40 saturate-[0.92]"
                        : "shadow-[0_1px_2px_-1px_rgba(16,24,40,0.04),0_3px_8px_-2px_rgba(16,24,40,0.07),0_8px_18px_-6px_rgba(16,24,40,0.08)] hover:z-10 hover:-translate-y-1 hover:shadow-[0_2px_4px_-1px_rgba(16,24,40,0.05),0_6px_14px_-4px_rgba(16,24,40,0.09),0_12px_24px_-8px_rgba(16,24,40,0.15)] hover:ring-primary/40",
                    )}
                  >
                    <img
                      alt={template}
                      loading={index < 6 ? "eager" : "lazy"}
                      src={`/templates/jpg/${template}.jpg`}
                      className="aspect-[1/1.4142] h-full w-auto object-cover object-top"
                      onError={(event) => {
                        const card = event.currentTarget.closest("[data-tpl-card]");
                        if (card instanceof HTMLElement) card.style.display = "none";
                      }}
                    />

                    <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-center bg-gradient-to-t from-black/35 to-transparent p-4 opacity-0 transition-opacity duration-300 group-hover/card:opacity-100">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-white/95 px-4 py-1.5 text-sm font-semibold text-neutral-900 shadow-md backdrop-blur">
                        {t`Preview PDF`}
                        <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M14 5l7 7m0 0l-7 7m7-7H3"
                          />
                        </svg>
                      </span>
                    </div>
                  </a>
                );
              })}
            </div>

            {/* Edge fades so cards dissolve into the background */}
            <div className="pointer-events-none absolute inset-y-0 left-0 z-20 w-16 bg-gradient-to-r from-background via-background/60 to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 z-20 w-16 bg-gradient-to-l from-background via-background/60 to-transparent" />
          </div>
        </div>

        {/* Controls: previous, pagination dots, next, play/pause */}
        <div className="mt-8 flex items-center justify-center gap-4">
          <button
            type="button"
            aria-label={t`Previous template`}
            onClick={() => {
              nudge(-1);
            }}
            className="flex size-10 items-center justify-center rounded-full border border-border bg-background text-primary shadow-[inset_0_1px_0_rgba(255,255,255,0.5),0_1px_2px_rgba(16,24,40,0.05),0_4px_12px_-2px_rgba(16,24,40,0.10)] transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.5),0_2px_6px_rgba(16,24,40,0.08),0_10px_24px_-4px_rgba(16,24,40,0.16)]"
          >
            <svg className="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="flex items-center gap-2">
            {Array.from({ length: PAGES }).map((_, index) => (
              <button
                key={index}
                type="button"
                aria-label={t`Go to page` + ` ${index + 1}`}
                onClick={() => {
                  goToDot(index);
                }}
                className={cn(
                  "h-2.5 rounded-full transition-all duration-300",
                  index === activeDot ? "w-10 bg-primary" : "w-2.5 bg-border hover:bg-foreground/30",
                )}
              />
            ))}
          </div>

          <button
            type="button"
            aria-label={t`Next template`}
            onClick={() => {
              nudge(1);
            }}
            className="flex size-10 items-center justify-center rounded-full border border-border bg-background text-primary shadow-[inset_0_1px_0_rgba(255,255,255,0.5),0_1px_2px_rgba(16,24,40,0.05),0_4px_12px_-2px_rgba(16,24,40,0.10)] transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.5),0_2px_6px_rgba(16,24,40,0.08),0_10px_24px_-4px_rgba(16,24,40,0.16)]"
          >
            <svg className="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <button
            type="button"
            aria-label={isPlaying ? t`Pause` : t`Resume scrolling`}
            onClick={() => {
              setIsPlaying((value) => !value);
            }}
            className={cn(
              "flex size-10 items-center justify-center rounded-full shadow-[inset_0_1px_0_rgba(255,255,255,0.4),0_1px_2px_rgba(16,24,40,0.05),0_4px_12px_-2px_rgba(16,24,40,0.10)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.4),0_2px_6px_rgba(16,24,40,0.08),0_10px_24px_-4px_rgba(16,24,40,0.16)]",
              isPlaying
                ? "border border-border bg-background text-primary hover:border-primary/30"
                : "bg-primary text-primary-foreground",
            )}
          >
            {isPlaying ? (
              <svg className="size-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            ) : (
              <svg className="size-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </button>
        </div>
      </div>
    </section>
  );
};
